import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { Syringe, CalendarIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const OFFERED = ["TDAP", "Flu", "Covid", "Hepatitis"];
const EMPTY = { name: "", phone: "", email: "", custom_request: "" };

export default function Immunization() {
  const [form, setForm] = useState(EMPTY);
  const [vaccine, setVaccine] = useState("Flu");
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isCustom = vaccine === "Other";

  async function submit(e) {
    e.preventDefault();
    if (!date) return toast.error("Please choose a preferred date.");
    if (isCustom && !form.custom_request.trim()) return toast.error("Please describe the vaccine you're requesting.");
    setLoading(true);
    try {
      await api.post("/immunizations/requests", {
        ...form,
        vaccine: isCustom ? "Other (not offered)" : vaccine,
        preferred_date: format(date, "yyyy-MM-dd"),
      });
      toast.success("Immunization request submitted! We'll confirm your appointment.");
      setForm(EMPTY); setVaccine("Flu"); setDate(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><Syringe className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Immunizations</h1>
          <p className="text-sm text-muted-foreground">Request an appointment for a vaccine we offer, or ask about one we don't.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-5 rounded-2xl border border-border bg-card p-6 sm:p-8">
        <div>
          <Label className="mb-3 block">Select a vaccine</Label>
          <div className="flex flex-wrap gap-2">
            {[...OFFERED, "Other"].map((v) => (
              <button
                key={v}
                type="button"
                data-testid={`immun-vaccine-${v.toLowerCase()}`}
                onClick={() => setVaccine(v)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                  vaccine === v ? "border-primary bg-primary text-white" : "border-border bg-white text-foreground hover:border-primary hover:text-primary"
                }`}
              >
                {v === "Other" ? "Something else" : v}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">We offer: TDAP, Flu, Covid, and Hepatitis.</p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="immun-name" value={form.name} onChange={set("name")} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone number</Label>
            <Input id="phone" data-testid="immun-phone" value={form.phone} onChange={set("phone")} required />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" data-testid="immun-email" value={form.email} onChange={set("email")} required />
        </div>

        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
          <p className="font-semibold text-foreground">Running a facility drive?</p>
          <p className="text-muted-foreground">Request an on-site vaccine drive for your staff and residents.</p>
          <Link to="/vaccine-drive" data-testid="immun-vaccine-drive-link" className="mt-2 inline-flex font-medium text-primary hover:underline">Request a Vaccine Drive →</Link>
        </div>

        <div className="space-y-2">
          <Label>Preferred date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="immun-date" className="w-full justify-start rounded-md font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
            </PopoverContent>
          </Popover>
        </div>

        {isCustom && (
          <div className="space-y-2">
            <Label htmlFor="custom">Which vaccine are you requesting?</Label>
            <Textarea id="custom" data-testid="immun-custom" placeholder="Tell us which immunization you need" value={form.custom_request} onChange={set("custom_request")} />
          </div>
        )}

        <Button type="submit" disabled={loading} data-testid="immun-submit" className="w-full rounded-full sm:w-auto sm:px-10">
          {loading ? "Submitting..." : "Request Appointment"}
        </Button>
      </form>
    </div>
  );
}
